import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { CalendarDays, Clock, MapPin, Users, Plus, CalendarPlus } from "lucide-react";
import { PageBanner } from "@/components/ui/PageBanner";
import { IMG } from "@/lib/assets";
import { COACH } from "@/lib/coach";
import { CoachAvatar } from "@/components/ui/CoachAvatar";
import { useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";

function classImage(c) {
  const t = `${c.title} ${c.description}`.toLowerCase();
  if (t.includes("tai chi")) return IMG.taichi;
  if (t.includes("yoga") || t.includes("vinyasa")) return IMG.yoga;
  if (t.includes("mobility") || t.includes("restor") || t.includes("medit")) return IMG.meditation;
  if (t.includes("hiit") || t.includes("metcon") || t.includes("condition") || t.includes("interval")) return IMG.conditioning;
  return IMG.strength;
}

function gcalUrl(c) {
  const start = new Date(c.starts_at);
  const end = new Date(start.getTime() + (c.duration_min || 60) * 60000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const p = new URLSearchParams({
    action: "TEMPLATE", text: `OCTON GYM · ${c.title}`,
    dates: `${fmt(start)}/${fmt(end)}`,
    details: c.description || "", location: c.location || "OCTON GYM",
  });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

function fmt(iso) {
  const d = new Date(iso);
  return { day: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
           time: d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) };
}

export default function Schedule() {
  const { user } = useAuth();
  const isTrainer = user?.role === "trainer" || user?.role === "admin";
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: classes = [] } = useQuery({ queryKey: ["classes"], queryFn: async () => (await api.get("/api/classes")).data });

  const book = async (c) => {
    try {
      if (c.is_booked) { await api.post(`/api/classes/${c.id}/cancel`); toast("Booking cancelled"); }
      else { await api.post(`/api/classes/${c.id}/book`); toast.success("Booked"); }
      qc.invalidateQueries({ queryKey: ["classes"] });
    } catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.conditioning} height="h-56 md:h-64" overline="bookings" title="Classes & Scheduling"
        subtitle="Book live sessions with your OCTON coach. Strength, HIIT, mobility, yoga and more, every week."
        actions={isTrainer ? <CreateClass onDone={() => qc.invalidateQueries({ queryKey: ["classes"] })} /> : null} />

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-xl border border-[#1E222A] bg-gradient-to-r from-[#10131A] via-[#0D1016] to-[#0B0D11] p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5" data-testid="meet-coach">
        <div className="absolute -left-16 -top-16 w-64 h-64 rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: "#CCFF00" }} />
        <CoachAvatar size={84} />
        <div className="relative flex-1">
          <span className="mono-label text-acid">your coach</span>
          <h2 className="font-heading font-black text-2xl tracking-tight">{COACH.name}</h2>
          <p className="text-[#B7BECB] text-sm mt-1 max-w-xl">{COACH.intro}</p>
        </div>
        <Button onClick={() => navigate("/app/coach")} className="relative bg-acid text-black hover:bg-[#AACC00] rounded-full h-11 px-5 shadow-[0_0_30px_rgba(204,255,0,0.35)]">
          <MessageCircle className="w-4 h-4" /> Talk to your coach
        </Button>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {classes.map((c, i) => {
          const t = fmt(c.starts_at);
          const full = c.booked_count >= c.capacity;
          return (
            <motion.div key={c.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} whileHover={{ y: -4 }}
              className={`relative rounded-xl overflow-hidden flex flex-col border bg-[#0F1115] ${c.is_booked ? "border-acid/60 shadow-[0_0_30px_rgba(204,255,0,0.15)]" : "border-[#1E222A]"}`} data-testid={`class-card-${i}`}>
              <div className="relative h-40 overflow-hidden group">
                <img src={classImage(c)} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(0deg,#0F1115 2%,rgba(15,17,21,0.1) 70%)" }} />
                <div className="absolute top-3 left-3 rounded-lg bg-black/70 backdrop-blur px-3 py-1.5 text-center border border-white/10">
                  <p className="mono-label text-acid leading-none">{t.day.split(" ")[0]}</p>
                  <p className="font-heading font-black text-lg leading-tight">{t.time}</p>
                </div>
                {c.is_booked && <span className="absolute top-3 right-3 mono-label bg-acid text-black px-2 py-1 rounded-full">booked</span>}
                {c.club_id && !c.is_booked && <span className="absolute top-3 right-3 mono-label bg-[#3D7BFF] text-black px-2 py-1 rounded-full">your club</span>}
                <h3 className="absolute bottom-2 left-4 right-4 font-heading font-black text-xl tracking-tight">{c.title}</h3>
              </div>
              <div className="p-5 pt-3 flex flex-col flex-1">
              <p className="text-[#8A91A6] text-sm flex-1">{c.description || (c.club_id ? `A ${c.location || "club"} session run by ${c.trainer_name || "your coach"}. Book your place so your coach knows you're coming, and it lands in your Training Diary.` : "")}</p>
              <div className="mt-4 flex items-center gap-3">
                <CoachAvatar size={30} ring={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white/90 truncate">{c.trainer_name === "Coach Vega" ? COACH.name : c.trainer_name}</p>
                  <p className="mono-label flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.location} · <Clock className="w-3 h-3" /> {c.duration_min || 60} min</p>
                </div>
              </div>
              <div className="mt-3">
                <div className="flex justify-between mono-label mb-1"><span className="flex items-center gap-1"><Users className="w-3 h-3" /> {c.booked_count}/{c.capacity} booked</span><span>{t.day}</span></div>
                <div className="h-1.5 rounded-full bg-[#1E222A] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, (c.booked_count / Math.max(c.capacity, 1)) * 100)}%`, background: full ? "#FF6B6B" : "#CCFF00" }} />
                </div>
              </div>
              <a href={gcalUrl(c)} target="_blank" rel="noopener noreferrer" data-testid={`gcal-${i}`}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-neural hover:text-acid transition-colors">
                <CalendarPlus className="w-3.5 h-3.5" /> Add to Google Calendar
              </a>
              {!isTrainer && (
                <Button onClick={() => book(c)} disabled={full && !c.is_booked} data-testid={`book-btn-${i}`}
                  className={`mt-4 rounded-md h-10 transition-colors ${
                    c.is_booked ? "bg-white/5 border border-[#1E222A] hover:bg-pulseError/20 hover:text-pulseError"
                      : "bg-acid text-black hover:bg-[#AACC00]"}`}>
                  {c.is_booked ? "Cancel booking" : full ? "Class full" : "Book class"}
                </Button>
              )}
              </div>
            </motion.div>
          );
        })}
        {classes.length === 0 && <p className="text-[#565C6F] text-sm">No classes scheduled.</p>}
      </div>
    </div>
  );
}

function CreateClass({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", starts_at: "", duration_min: 60, capacity: 12, location: "OCTON Floor A" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/api/classes", { ...form, starts_at: new Date(form.starts_at).toISOString(), duration_min: Number(form.duration_min), capacity: Number(form.capacity) });
      toast.success("Class created"); setOpen(false); onDone?.();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-acid text-black hover:bg-[#AACC00] rounded-full" data-testid="new-class-btn"><Plus className="w-4 h-4" /> New class</Button>
      </DialogTrigger>
      <DialogContent className="surface border-[#1E222A]">
        <DialogHeader><DialogTitle className="font-heading">Create class</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="mono-label">Title</Label><Input value={form.title} onChange={set("title")} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" /></div>
          <div><Label className="mono-label">Description</Label><Input value={form.description} onChange={set("description")} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" /></div>
          <div><Label className="mono-label">Starts at</Label><Input type="datetime-local" value={form.starts_at} onChange={set("starts_at")} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mono-label">Capacity</Label><Input type="number" value={form.capacity} onChange={set("capacity")} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" /></div>
            <div><Label className="mono-label">Location</Label><Input value={form.location} onChange={set("location")} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy || !form.title || !form.starts_at} className="bg-acid text-black hover:bg-[#AACC00]">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
